import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { AuthService } from '@coin/shared/auth/data-access';
import { FrontendType, PermissionResource } from '@coin/shared/core/util-enums';
import { TinyHelpers } from '@coin/shared/core/util-helpers';
import { firstValueFrom, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { PermissionsService } from './permissions.service';
import { UserState } from './user.state';

export const authGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const userState = inject(UserState);
  const router = inject(Router);
  const permissionsService = inject(PermissionsService);

  // Allows users, that do not have the PermissionResource.AdminApp to access the according route.
  // Only the permission for the specific route is validated. Returns true if the user has all permissions of the
  // skipFrontendTypeCheckForRoles property in the admin routing module. Currently only in place for admin.
  const skipFrontendTypeCheck = (roles: PermissionResource[], frontendType: FrontendType): boolean =>
    frontendType === FrontendType.admin && roles && permissionsService.hasPermissions(...roles);

  const hasPermissions = (frontendType: FrontendType): boolean => {
    const frontendTypeResource =
      frontendType === FrontendType.admin ? PermissionResource.AdminApp : PermissionResource.CustomerApp;
    return permissionsService.hasPermissions(frontendTypeResource);
  };

  if (authService.isE2eTest$.value) {
    while (!(await authService.getEmulation())) {
      await TinyHelpers.sleep(20);
    }
  }

  if (authService.isAuthenticated$.value) {
    const requests$ = authService.isE2eTest$.value
      ? [userState.loadUser(true)]
      : [userState.loadUser(), userState.loadLoggedInUser()];
    return firstValueFrom(
      forkJoin(requests$).pipe(
        map(res => {
          if (skipFrontendTypeCheck(route.data?.skipFrontendTypeCheckForRoles, route.data.frontendType)) {
            return !!res;
          }

          if (!hasPermissions(route.data.frontendType ?? FrontendType.admin)) {
            return new RedirectCommand(router.createUrlTree(['unauthorized']));
          }
          return !!res;
        })
      )
    );
  }
  authService.login(state.url);
  return false;
};
